// src/routes/index.js
import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import Layout from 'components/layout';
import BasicLayout from 'components/layout/BasicLayout';
import LoadingSpinner from 'components/common/LoadingSpinner';

import Home from 'pages/home';

// const Home = lazy(() => import('pages/home'));
const Solution = lazy(() => import('pages/solution'));
const AttackSurfaceDiscover = lazy(() =>
  import('pages/solution/AttackSurfaceDiscover'),
);
const DomainSpoofingDetection = lazy(() =>
  import('pages/solution/DomainSpoofingDetection'),
);
const DataBreachDetection = lazy(() =>
  import('pages/solution/DataBreachDetection'),
);
const DarknetMonitoringMSSP = lazy(() =>
  import('pages/solution/DarknetMonitoringMSSP'),
);
const Knowledge = lazy(() => import('pages/Knowledge'));
const NewsDetail = lazy(() => import('pages/NewsDetail'));
const Pricing = lazy(() => import('pages/Pricing'));
const UseCases = lazy(() => import('pages/UseCases'));
const UseCasesCiso = lazy(() => import('pages/UseCasesCiso'));
const UseCasesThreat = lazy(() => import('pages/UseCasesThreat'));
const UseCasesSoc = lazy(() => import('pages/UseCasesSoc'));
const UseCasesFinance = lazy(() => import('pages/UseCasesFinance'));
const UseCasesHealthCare = lazy(() => import('pages/UseCasesHealthCare'));
const UseCasesEcommerce = lazy(() => import('pages/UseCasesEcommerce'));
const UseCasesMedia = lazy(() => import('pages/UseCasesMedia'));
const UseCasesGovernment = lazy(() => import('pages/UseCasesGovernment'));
const UseCasesEnergy = lazy(() => import('pages/UseCasesEnergy'));
const UseCasesTechnology = lazy(() => import('pages/UseCasesTechnology'));
const UseCasesEducation = lazy(() => import('pages/UseCasesEducation'));
const UseCasesLegal = lazy(() => import('pages/UseCasesLegal'));
const UseCasesInsurance = lazy(() => import('pages/UseCasesInsurance'));
const AboutUs = lazy(() => import('pages/AboutUs'));
const Calendly = lazy(() => import('pages/Calendly'));
const Glossary = lazy(() => import('pages/Glossary'));
const DarkWebMonitoring = lazy(() => import('pages/DarkWebMonitoring'));
const CredentialLeakDetection = lazy(() => import('pages/CredentialLeakDetection'));
const StealerLogMonitoring = lazy(() => import('pages/StealerLogMonitoring'));
const RansomwareLeakMonitoring = lazy(() => import('pages/RansomwareLeakMonitoring'));
const PasteSiteMonitoring = lazy(() => import('pages/PasteSiteMonitoring'));
const BotnetInfectionDetection = lazy(() => import('pages/BotnetInfectionDetection'));
const ExposedPasswordMonitoring = lazy(() => import('pages/ExposedPasswordMonitoring'));
const EmailCompromiseMonitoring = lazy(() => import('pages/EmailCompromiseMonitoring'));
const WhatIsDarkWeb = lazy(() => import('pages/WhatIsDarkWeb'));
const CyberLaws = lazy(() => import('pages/CyberLaws'));
const CompareIndex = lazy(() => import('pages/compare/CompareIndex'));
const CompareDetail = lazy(() => import('pages/compare/CompareDetail'));
const Documentation = lazy(() => import('pages/Documentation'));
const FrequentlyAskedQuestions = lazy(() => import('pages/FrequentlyAskedQuestions'));
const PrivacyPolicy = lazy(() => import('pages/etc/PrivacyPolicy'));
const Imprint = lazy(() => import('pages/etc/Imprint'));
const ToS = lazy(() => import('pages/etc/ToS'));
const SpainPage = lazy(() => import('pages/countries/Spain'));
const SpainEmailLeakCheck = lazy(() => import('pages/spain/ComprobarEmailFiltrado'));
const SpainCompanyDataLeak = lazy(() => import('pages/spain/DetectarFugaDatosEmpresa'));
const SpainBreachMonitoring = lazy(() => import('pages/spain/MonitorizacionBrechasDatos'));
const SpainStealerLogs = lazy(() => import('pages/spain/AnalisisStealerLogs'));
const SpainBreachAlerts = lazy(() => import('pages/spain/AlertasBrechasEspana'));
const SpainThreatIntel = lazy(() => import('pages/spain/ThreatIntelligenceEspana'));
const FrancePage = lazy(() => import('pages/countries/France'));
const FranceVerifyEmailLeak = lazy(() => import('pages/france/VerifierEmailFuite'));
const FranceCompanyDataLeak = lazy(() => import('pages/france/DetecterFuiteDonneesEntreprise'));
const FranceBreachMonitoring = lazy(() => import('pages/france/SurveillanceBrechesDonnees'));
const FranceStealerLogs = lazy(() => import('pages/france/AnalyseStealerLogs'));
const FranceBreachAlerts = lazy(() => import('pages/france/AlertesBrechesFrance'));
const GermanyPage = lazy(() => import('pages/countries/Germany'));
const GermanyEmailLeakCheck = lazy(() => import('pages/germany/EmailLeakPruefen'));
const GermanyCompanyDataLeak = lazy(() => import('pages/germany/DatenleckUnternehmenErkennen'));
const GermanyBreachMonitoring = lazy(() => import('pages/germany/DatenleckUeberwachung'));
const GermanyStealerLogs = lazy(() => import('pages/germany/StealerLogsAnalyse'));
const GermanyBreachAlerts = lazy(() => import('pages/germany/DatenleckWarnungenDeutschland'));
const FreeTrial = lazy(() => import('pages/FreeTrial'));
const Contact = lazy(() => import('pages/Contact'));
const Reports = lazy(() => import('pages/Reports'));
const DarknetReport24H = lazy(() => import('pages/DarknetReport24H'));
const ThreatCaseStudies = lazy(() => import('pages/ThreatCaseStudies'));
const Sitemap = lazy(() => import('pages/Sitemap'));
const DataCategory = lazy(() => import('pages/DataCategory'));
const MsspLanding = lazy(() => import('pages/MsspLanding'));
const MsspSpanishLanding = lazy(() => import('pages/MsspSpanishLanding'));

const LogIn = lazy(() => import('pages/Login'));

const CustomerStories = lazy(() => import('pages/CustomerStories'));
const SubscriberNotif = lazy(() => import('components/common/SubscriberNotif'));
const Partners = lazy(() => import('pages/DarkWebPartners'));

const NotFoundPage = lazy(() => import('pages/common/NotFoundPage'));
const ServerErrorPage = lazy(() => import('pages/common/ServerErrorPage'));

// Define routes
export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <Layout />
      </Suspense>
    ),
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: 'solution/',
        children: [
          {
            index: true,
            element: <Solution />,
          },
          {
            path: 'attack-surface-discover',
            element: <AttackSurfaceDiscover />,
          },
          {
            path: 'domain-spoofing-detection',
            element: <DomainSpoofingDetection />,
          },
          {
            path: 'data-breach-detection',
            element: <DataBreachDetection />,
          },
          {
            path: 'darknet-monitoring-mssp',
            element: <DarknetMonitoringMSSP />,
          },
        ],
      },
      {
        path: 'knowledge/',
        children: [
          {
            index: true,
            element: <Knowledge />,
          },
          {
            path: 'news/:lang/:slug',
            element: <NewsDetail />,
          },
        ],
      },
      {
        path: 'glossary/',
        children: [
          {
            index: true,
            element: <Glossary />,
          },
        ],
      },
      {
        path: 'compare/',
        children: [
          {
            index: true,
            element: <CompareIndex />,
          },
          {
            path: ':slug',
            element: <CompareDetail />,
          },
        ],
      },
      {
        path: 'pricing',
        element: <Pricing />,
      },
      {
        path: 'documentation',
        element: <Documentation />,
      },
      {
        path: 'frequently-asked-questions',
        element: <FrequentlyAskedQuestions />,
      },
      {
        path: 'free-trial',
        element: <FreeTrial />,
      },
      {
        path: 'contact',
        element: <Contact />,
      },
      {
        path: 'reports',
        element: <Reports />,
      },
      {
        path: 'darknet-report-24h',
        element: <DarknetReport24H />,
      },
      {
        path: 'threat-case-studies',
        element: <ThreatCaseStudies />,
      },
      {
        path: 'sitemap',
        element: <Sitemap />,
      },
      {
        path: 'data-records',
        element: <DataCategory pageKey="data-records" />,
      },
      {
        path: 'breached-websites',
        element: <DataCategory pageKey="breached-websites" />,
      },
      {
        path: 'botnet-records',
        element: <DataCategory pageKey="botnet-records" />,
      },
      {
        path: 'compromised-bots',
        element: <DataCategory pageKey="compromised-bots" />,
      },
      {
        path: 'credit-card-leaks',
        element: <DataCategory pageKey="credit-card-leaks" />,
      },
      {
        path: 'pastebin-leaks',
        element: <DataCategory pageKey="pastebin-leaks" />,
      },
      {
        path: 'use-cases',
        element: <UseCases />,
      },
      {
        path: 'use-cases/ciso',
        element: <UseCasesCiso />,
      },
      {
        path: 'use-cases/threat',
        element: <UseCasesThreat />,
      },
      {
        path: 'use-cases/soc',
        element: <UseCasesSoc />,
      },
      {
        path: 'use-cases/finance-industry',
        element: <UseCasesFinance />,
      },
      {
        path: 'use-cases/healthcare-industry',
        element: <UseCasesHealthCare />,
      },
      {
        path: 'use-cases/ecommerce-industry',
        element: <UseCasesEcommerce />,
      },
      {
        path: 'use-cases/media-industry',
        element: <UseCasesMedia />,
      },
      {
        path: 'use-cases/government-industry',
        element: <UseCasesGovernment />,
      },
      {
        path: 'use-cases/energy-industry',
        element: <UseCasesEnergy />,
      },
      {
        path: 'use-cases/technology-industry',
        element: <UseCasesTechnology />,
      },
      {
        path: 'use-cases/education-industry',
        element: <UseCasesEducation />,
      },
      {
        path: 'use-cases/legal-industry',
        element: <UseCasesLegal />,
      },
      {
        path: 'use-cases/insurance-industry',
        element: <UseCasesInsurance />,
      },
      {
        path: 'about-us',
        element: <AboutUs />,
      },
      {
        path: 'calendly',
        element: <Calendly />,
      },
      {
        path: 'dark-web-monitoring',
        element: <DarkWebMonitoring />,
      },
      {
        path: 'credential-leak-detection',
        element: <CredentialLeakDetection />,
      },
      {
        path: 'stealer-log-monitoring',
        element: <StealerLogMonitoring />,
      },
      {
        path: 'ransomware-leak-monitoring',
        element: <RansomwareLeakMonitoring />,
      },
      {
        path: 'paste-site-monitoring',
        element: <PasteSiteMonitoring />,
      },
      {
        path: 'botnet-infection-detection',
        element: <BotnetInfectionDetection />,
      },
      {
        path: 'exposed-password-monitoring',
        element: <ExposedPasswordMonitoring />,
      },
      {
        path: 'email-compromise-monitoring',
        element: <EmailCompromiseMonitoring />,
      },
      {
        path: 'what-is-dark-web',
        element: <WhatIsDarkWeb />,
      },
      {
        path: 'cyber-laws',
        element: <CyberLaws />,
      },
      {
        path: 'privacy-policy',
        element: <PrivacyPolicy />,
      },
      {
        path: 'imprint',
        element: <Imprint />,
      },
      {
        path: 'tos',
        element: <ToS />,
      },
      {
        path: 'es',
        children: [
          {
            index: true,
            element: <SpainPage />,
          },
          {
            path: 'comprobar-email-filtrado',
            element: <SpainEmailLeakCheck />,
          },
          {
            path: 'detectar-fuga-datos-empresa',
            element: <SpainCompanyDataLeak />,
          },
          {
            path: 'monitorizacion-brechas-datos',
            element: <SpainBreachMonitoring />,
          },
          {
            path: 'analisis-stealer-logs',
            element: <SpainStealerLogs />,
          },
          {
            path: 'alertas-brechas-espana',
            element: <SpainBreachAlerts />,
          },
          {
            path: 'threat-intelligence',
            element: <SpainThreatIntel />,
          },
        ],
      },
      {
        path: 'fr',
        children: [
          {
            index: true,
            element: <FrancePage />,
          },
          {
            path: 'verifier-email-fuite',
            element: <FranceVerifyEmailLeak />,
          },
          {
            path: 'detecter-fuite-donnees-entreprise',
            element: <FranceCompanyDataLeak />,
          },
          {
            path: 'surveillance-breches-donnees',
            element: <FranceBreachMonitoring />,
          },
          {
            path: 'analyse-stealer-logs',
            element: <FranceStealerLogs />,
          },
          {
            path: 'alertes-breches-france',
            element: <FranceBreachAlerts />,
          },
        ],
      },
      {
        path: 'de',
        children: [
          {
            index: true,
            element: <GermanyPage />,
          },
          {
            path: 'email-leak-pruefen',
            element: <GermanyEmailLeakCheck />,
          },
          {
            path: 'datenleck-unternehmen-erkennen',
            element: <GermanyCompanyDataLeak />,
          },
          {
            path: 'datenleck-ueberwachung',
            element: <GermanyBreachMonitoring />,
          },
          {
            path: 'stealer-logs-analyse',
            element: <GermanyStealerLogs />,
          },
          {
            path: 'datenleck-warnungen-deutschland',
            element: <GermanyBreachAlerts />,
          },
        ],
      },
      {
        path: 'customer-success-stories/',
        children: [
          {
            index: true,
            element: <CustomerStories />,
          },
           {
            path: 'case-study/:slug',
            element: <NewsDetail />,
          },
        ],
      },
      {
        path: 'subscription-confirmed',
        element: <SubscriberNotif />,
      },
      {
        path: 'partners',
        element: <Partners />,
      },
    ],
  },
  {
    path: 'login',
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <LogIn />
      </Suspense>
    ),
  },
  {
    path: 'lp/en/mssp',
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <MsspLanding />
      </Suspense>
    ),
  },
  {
    path: 'lp/es/mssp',
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <MsspSpanishLanding />
      </Suspense>
    ),
  },
  {
    path: '500',
    element: (
      <Suspense fallback={<LoadingSpinner />}>
        <BasicLayout>
          <ServerErrorPage />
        </BasicLayout>
      </Suspense>
    ),
  },
  
  {
    path: '*',
    element: <Navigate to="/knowledge" replace />,
    // element: (
    //   <Suspense fallback={<LoadingSpinner />}>
    //     <BasicLayout>
    //       <NotFoundPage />
    //     </BasicLayout>
    //   </Suspense>
    // ),
  },

]);
